import React, { useState, useEffect, useCallback } from 'react';
import PrimaryRail from './PrimaryRail';
import SecondaryRail from './SecondaryRail';
import FilterChip from './FilterChip';

/**
 * RailNavigation - Main component that combines PrimaryRail and SecondaryRail
 * Provides sticky two-row navigation for mobile sports dashboards
 * 
 * @param {string} sport - Current sport context
 * @param {Array} primaryTabs - Tabs for primary rail
 * @param {Array} filters - Filter configuration for secondary rail
 * @param {Function} onFilterChange - Callback when any filter changes
 * @param {Function} onTabChange - Callback when primary tab changes
 * @param {string} activeTab - Currently active tab
 * @param {Function} onReset - Callback to reset all filters
 * @param {boolean} hasActiveFilters - Whether any filters are active (non-default)
 * @param {string} className - Additional CSS classes
 * @param {ReactNode} children - Optional child components
 */
const RailNavigation = ({
  sport,
  primaryTabs = [],
  filters = [],
  onFilterChange,
  onTabChange,
  activeTab,
  onReset,
  hasActiveFilters = false,  // Accept as prop
  className = '',
  children
}) => {
  const [isSticky, setIsSticky] = useState(false);
  
  // Note: hasActiveFilters should be passed as a prop from the sport-specific hook
  // This ensures consistent filter state management across all sports

  // Handle scroll for sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsSticky(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure filter change propagates correctly
  const handleFilterChange = useCallback((filterId, filterData) => {
    if (onFilterChange) {
      onFilterChange(filterId, filterData);
    }
  }, [onFilterChange]);

  // Ensure tab change propagates correctly
  const handleTabChange = useCallback((tabId) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
  }, [onTabChange]);

  return (
    <div 
      className={`
        bg-black/5 backdrop-blur-sm
        ${className}
      `}
      role="navigation"
      aria-label="Filter navigation"
    >
      {/* Primary Rail - Sport/League Tabs */}
      <PrimaryRail
        tabs={primaryTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        variant={sport === 'all' ? 'sport' : 'league'}
      />
      
      {/* Secondary Rail - Filter Chips */}
      <SecondaryRail
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={onReset}
        hasActiveFilters={hasActiveFilters}
      />
      
      {/* Optional slot for custom components */}
      {children && (
        <div className="px-4 py-2 bg-black/10 border-b border-white/10">
          {children}
        </div>
      )}
    </div>
  );
};

// Export sub-components for flexibility
RailNavigation.PrimaryRail = PrimaryRail;
RailNavigation.SecondaryRail = SecondaryRail;
RailNavigation.FilterChip = FilterChip;

// Custom slot component for sport-specific additions
RailNavigation.Custom = ({ slot, children }) => {
  if (slot === 'end') {
    return <div className="ml-auto">{children}</div>;
  }
  return children;
};

export default RailNavigation;